import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Stack, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import MDialog from '../../../../atoms/MDialog';
import MButton from '../../../../MUI/Button/MButton';
import ShadowBlock from '../../../../atoms/ShadowBlock';
import Table from '../../../../atoms/Table/Table';
import EmptyBlock from '../../../../molecules/EmptyBlock';
import { fixProblemTableColumns } from './FixProblemDialog.constant';
import { apiClient } from '../../../../../lib/apiClient';
import notificationUtils from '../../../../../utils/notificationUtils';
import LoaderCover from '../../../../atoms/LoaderCover';
import Field from '../../../../atoms/Field';
import SearchInputWithLabel from '../../../../atoms/SearchInputWithLabel/SearchInputWithLabel';
import Form from '../../../../atoms/Form';

export const FixProblemDialog = ({ isOpen, onClose, onConfirm, oldValue }) => {
  const form = useForm();

  const [isLoading, setIsLoading] = useState(false);
  const [tableLoading, setIsTableLoader] = useState(false);
  const [qboMappingId, setQboMappingId] = useState(null);
  const [entities, setEntities] = useState(null);

  const name = form.watch('name');

  const handleConfirm = () => {
    onConfirm({ problemId: oldValue?.problemId, quickBooksId: qboMappingId });
    onClose();
  };

  const updateValue = (originalId) => {
    setQboMappingId((prev) => (prev === originalId ? null : originalId));
  };

  const handleFetchDataPage = async (currentPage = 1) => {
    setIsTableLoader(true);

    const params = {
      pageSize: 10,
      currentPage,
      mappingEntityType: oldValue?.entity,
      name,
    };

    try {
      const res = await apiClient.post(`/api/quickBooksMappingProblems/filter/entities`, params);

      setEntities(res.data.result?.items?.length ? res.data.result : []);
    } catch {
      notificationUtils.error('Failed, try again later');
    } finally {
      setIsTableLoader(false);
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      setQboMappingId(null);
      handleFetchDataPage();
    } catch (e) {
      notificationUtils.error('Unknown record name');
      setEntities(null);
    }
    setIsLoading(false);
  };

  const columns = useMemo(
    () => fixProblemTableColumns({ updateValue, qboMappingId }),
    [qboMappingId],
  );

  useEffect(() => {
    handleFetchDataPage();
  }, []);

  return (
    <MDialog
      isOpen={isOpen}
      onClose={onClose}
      onClosed={onClose}
      title="Set a new QBO ID"
      maxWidth="1300px"
      fullWidth
      footerActions={[
        <MButton variant="secondary" size="large" onClick={onClose}>
          Cancel
        </MButton>,
        <MButton type="submit" size="large" disabled={!qboMappingId} onClick={handleConfirm}>
          Confirm
        </MButton>,
      ]}
    >
      <Stack gap={2} padding={0}>
        <Stack
          gap={2}
          padding={0}
          flexDirection="row"
          justifyContent="space-between"
          flexWrap="wrap"
        >
          <Stack gap={1} alignItems="center" flexDirection="row" padding={0}>
            <Typography variant="buttonLarge" color="text.secondary">
              Record Name:{' '}
            </Typography>
            <Typography variant="bodyL500">{oldValue?.name}</Typography>
          </Stack>
          <Stack gap={1} alignItems="center" flexDirection="row" padding={0}>
            <Typography variant="buttonLarge" color="text.secondary">
              Current QBO ID:{' '}
            </Typography>
            <Typography variant="bodyL500">{oldValue?.assumedMappingId}</Typography>
          </Stack>
          <Stack gap={1} alignItems="center" flexDirection="row" padding={0}>
            <Typography variant="buttonLarge" color="text.secondary">
              Entity Type:{' '}
            </Typography>
            <Typography variant="bodyL500">{oldValue?.entity}</Typography>
          </Stack>
        </Stack>
        <Form form={form} onSubmit={handleSubmit}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap">
            <Stack direction="row">
              <Field
                name="name"
                render={({ field, onCustomChange }) => (
                  <SearchInputWithLabel
                    label="Record Name"
                    placeholder="Enter name"
                    onChangeValue={onCustomChange(field.onChange)}
                    name="name"
                    value={field.value}
                    width={300}
                  />
                )}
              />
              <MButton
                data-testid="apply_btn"
                sx={{ marginLeft: '24px' }}
                variant="secondary"
                type="submit"
              >
                Apply
              </MButton>
            </Stack>
          </Stack>
        </Form>
        {isLoading && <LoaderCover />}
        {entities && !isLoading && (
          <Box width="100%" margin="0 auto">
            <ShadowBlock>
              <Stack position="relative">
                {entities?.items?.length ? (
                  <Box display="flex" alignItems="center">
                    <Table
                      data-testid="entities-table"
                      columns={columns}
                      rows={entities.items ?? []}
                      total={entities?.totalRowsCount}
                      isPagination={entities?.totalPages > 1}
                      onNextPage={handleFetchDataPage}
                      onPreviousPage={handleFetchDataPage}
                      onGotoPage={handleFetchDataPage}
                      onRefreshPage={handleFetchDataPage}
                      pageSize={10}
                      loading={tableLoading}
                      maxHeight="32vh"
                    />
                  </Box>
                ) : (
                  <Box display="flex" justifyContent="center" alignItems="center" width="100%">
                    <EmptyBlock title="No Records" />
                  </Box>
                )}
              </Stack>
            </ShadowBlock>
          </Box>
        )}
      </Stack>
    </MDialog>
  );
};

FixProblemDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  oldValue: PropTypes.shape({
    assumedMappingId: PropTypes.string,
    name: PropTypes.string,
    problemId: PropTypes.string,
    entity: PropTypes.string,
  }).isRequired,
};

FixProblemDialog.defaultProps = {};
